<script setup lang="ts">
import AppTabs from "@/components/shared/AppTabs.vue";
import CatMascot from "@/components/doctor-performance/cats/CatMascot.vue";
import TerritoryCoverageDialog from "@/components/territory-insights/TerritoryCoverageDialog.vue";
import TerritoryDetailDrawer from "@/components/territory-insights/TerritoryDetailDrawer.vue";
import TerritoryMap from "@/components/territory-insights/TerritoryMap.vue";
import { METRIC_LOOK } from "@/components/territory-insights/territoryInsightsUi";
import { createTerritoryInsights, provideTerritoryInsights } from "@/composables/useTerritoryInsights";
import type { MapLens } from "@/type/territoryInsights";
import { TERRITORY_METRICS } from "@/type/territoryInsights";
import type { TabItem } from "@/type/tabs";
import { TERRITORY_PERIOD_PRESETS } from "@/utils/territoryInsights";

/**
 * Customer territory insights — where the paying customers live, what animals they keep, where
 * the business is growing and where to aim the next campaign. A heat map stays beside eight tabs;
 * each tab sets what the map shows. Reached by URL only (no sidebar or search entry); access is
 * the `page.customers.territory_insights` page permission. Until `get_territory_dashboard` is
 * deployed the page runs on sample data and says so with a chip; see
 * docs/README.customer-territory-dashboard-backend-prompt.md.
 */

definePage({ meta: { permissionKey: "page.customers.territory_insights" } });

const { t } = useI18n({ useScope: "global" });
const ctx = createTerritoryInsights();
provideTerritoryInsights(ctx);

const activeTab = ref("");
const mapCollapsed = ref(false);

const tabs: TabItem[] = [
  { value: "overview", labelKey: "alkokh.territoryInsights.tabs.overview", icon: "tabler-layout-dashboard", loader: () => import("@/components/territory-insights/tabs/TerritoryInsightsOverviewTab.vue") },
  { value: "territories", labelKey: "alkokh.territoryInsights.tabs.territories", icon: "tabler-map-pins", loader: () => import("@/components/territory-insights/tabs/TerritoryInsightsTerritoriesTab.vue") },
  { value: "customers", labelKey: "alkokh.territoryInsights.tabs.customers", icon: "tabler-crown", loader: () => import("@/components/territory-insights/tabs/TerritoryInsightsTopCustomersTab.vue") },
  { value: "animals", labelKey: "alkokh.territoryInsights.tabs.animals", icon: "tabler-paw", loader: () => import("@/components/territory-insights/tabs/TerritoryInsightsAnimalsTab.vue") },
  { value: "trends", labelKey: "alkokh.territoryInsights.tabs.trends", icon: "tabler-trending-up", loader: () => import("@/components/territory-insights/tabs/TerritoryInsightsTrendsTab.vue") },
  { value: "retention", labelKey: "alkokh.territoryInsights.tabs.retention", icon: "tabler-heart-broken", loader: () => import("@/components/territory-insights/tabs/TerritoryInsightsRetentionTab.vue") },
  { value: "targeting", labelKey: "alkokh.territoryInsights.tabs.targeting", icon: "tabler-target-arrow", loader: () => import("@/components/territory-insights/tabs/TerritoryInsightsTargetingTab.vue") },
  { value: "live", labelKey: "alkokh.territoryInsights.tabs.live", icon: "tabler-activity-heartbeat", loader: () => import("@/components/territory-insights/tabs/TerritoryInsightsLiveTab.vue") },
];

// Each tab decides what the map shows.
function lensFor(tab: string): MapLens {
  switch (tab) {
    case "customers": return { kind: "topCustomers" };
    case "animals": return { kind: "species" };
    case "trends": return { kind: "timeline" };
    case "retention": return { kind: "atRisk" };
    case "targeting": return { kind: "opportunity" };
    case "live": return { kind: "live" };
    default: return { kind: "metric", metric: ctx.metric.value };
  }
}
watch(activeTab, tab => (ctx.lens.value = lensFor(tab || "overview")), { immediate: true });

const presetItems = computed(() => TERRITORY_PERIOD_PRESETS.map(value => ({ value, title: t(`alkokh.territoryInsights.period.${value}`) })));

// "Updated 12 s ago" ticks on its own; the data does not refetch for it.
const now = useNow({ interval: 5000 });
const updatedAgo = computed(() => {
  const at = ctx.lastFeedAt.value ?? ctx.lastLoadedAt.value;
  if (!at)
    return "";
  const seconds = Math.max(0, Math.round((now.value.getTime() - at.getTime()) / 1000));

  return seconds < 60
    ? t("alkokh.territoryInsights.live.secondsAgo", { n: seconds })
    : t("alkokh.territoryInsights.live.minutesAgo", { n: Math.round(seconds / 60) });
});

const showTabs = computed(() => ctx.state.value === "ok" && ctx.loaded.value);
const firstLoad = computed(() => !ctx.loaded.value);
</script>

<template>
  <div class="ti-page">
    <VCard
      elevation="0"
      border
      class="ti-header mb-4"
    >
      <div class="ti-head">
        <div class="ti-head__text">
          <h1 class="text-h4 d-flex align-center gap-2 flex-wrap">
            {{ t("alkokh.territoryInsights.title") }}
            <VChip
              v-if="ctx.report.value.source === 'mock'"
              color="warning"
              variant="tonal"
              size="small"
              prepend-icon="tabler-flask"
            >
              {{ t("alkokh.territoryInsights.sampleData") }}
            </VChip>
            <VChip
              :color="ctx.live.value ? (ctx.liveState.value === 'failed' ? 'warning' : 'error') : 'default'"
              :variant="ctx.live.value ? 'flat' : 'tonal'"
              size="small"
              class="ti-live-chip"
              :aria-pressed="ctx.live.value"
              @click="ctx.live.value = !ctx.live.value"
            >
              <span
                class="ti-live-chip__dot"
                :class="{ 'ti-live-chip__dot--on': ctx.live.value }"
              />
              {{ ctx.live.value ? t("alkokh.territoryInsights.live.on") : t("alkokh.territoryInsights.live.paused") }}
              <span
                v-if="ctx.live.value && updatedAgo"
                class="ti-live-chip__ago"
              >· {{ updatedAgo }}</span>
            </VChip>
          </h1>
          <p class="text-body-2 text-medium-emphasis mb-0">
            {{ t("alkokh.territoryInsights.subtitle") }}
          </p>
        </div>
        <div
          class="ti-head__art"
          aria-hidden="true"
        >
          <span class="ti-head__pin ti-head__pin--a">📍</span>
          <span class="ti-head__pin ti-head__pin--b">🐱</span>
          <span class="ti-head__pin ti-head__pin--c">🐶</span>
          <span class="ti-head__pin ti-head__pin--d">🐦</span>
        </div>
      </div>

      <div class="ti-filters">
        <AppSelect
          v-model="ctx.preset.value"
          :items="presetItems"
          density="compact"
          :label="t('alkokh.territoryInsights.period.label')"
          class="ti-filters__item"
        />
        <template v-if="ctx.preset.value === 'custom'">
          <AppDateTimePicker
            v-model="ctx.customFrom.value"
            density="compact"
            :label="t('alkokh.territoryInsights.period.from')"
            :config="{ enableTime: false, dateFormat: 'Y-m-d' }"
            class="ti-filters__item"
          />
          <AppDateTimePicker
            v-model="ctx.customTo.value"
            density="compact"
            :label="t('alkokh.territoryInsights.period.to')"
            :config="{ enableTime: false, dateFormat: 'Y-m-d' }"
            class="ti-filters__item"
          />
        </template>
        <AppSelect
          v-model="ctx.branches.value"
          :items="ctx.branchItems.value"
          multiple
          chips
          closable-chips
          clearable
          density="compact"
          :label="t('alkokh.territoryInsights.filters.branches')"
          :placeholder="t('alkokh.territoryInsights.filters.allBranches')"
          class="ti-filters__item ti-filters__item--wide"
        />
        <VBtnToggle
          v-model="ctx.metric.value"
          mandatory
          density="compact"
          variant="outlined"
          divided
          color="primary"
          class="ti-filters__metric"
          :aria-label="t('alkokh.territoryInsights.filters.metric')"
        >
          <VBtn
            v-for="m in TERRITORY_METRICS"
            :key="m"
            :value="m"
            size="small"
            :prepend-icon="METRIC_LOOK[m].icon"
          >
            {{ t(`alkokh.territoryInsights.metrics.${m}`) }}
          </VBtn>
        </VBtnToggle>
        <div class="ti-filters__actions">
          <VProgressCircular
            v-if="ctx.loading.value"
            indeterminate
            size="20"
            width="2"
            color="primary"
          />
          <VBtn
            icon
            variant="text"
            size="small"
            :aria-label="t('alkokh.territoryInsights.actions.refresh')"
            @click="ctx.load()"
          >
            <VIcon icon="tabler-refresh" />
            <VTooltip activator="parent">
              {{ t("alkokh.territoryInsights.actions.refresh") }}
            </VTooltip>
          </VBtn>
          <VBtn
            icon
            variant="text"
            size="small"
            :aria-label="t('alkokh.territoryInsights.coverage.title')"
            @click="ctx.coverageOpen.value = true"
          >
            <VBadge
              :model-value="ctx.geo.value.unmapped.length > 0"
              :content="ctx.geo.value.unmapped.length"
              color="warning"
            >
              <VIcon icon="tabler-map-search" />
            </VBadge>
            <VTooltip activator="parent">
              {{ t("alkokh.territoryInsights.coverage.title") }}
            </VTooltip>
          </VBtn>
        </div>
      </div>
    </VCard>

    <div class="ti-layout">
      <section
        class="ti-layout__map"
        :class="{ 'ti-layout__map--collapsed': mapCollapsed }"
      >
        <VCard
          elevation="0"
          border
          class="ti-map-card"
        >
          <button
            type="button"
            class="ti-map-card__toggle"
            :aria-expanded="!mapCollapsed"
            @click="mapCollapsed = !mapCollapsed"
          >
            <VIcon
              icon="tabler-map-2"
              size="18"
            />
            <span>{{ t("alkokh.territoryInsights.map.title") }}</span>
            <VIcon
              :icon="mapCollapsed ? 'tabler-chevron-down' : 'tabler-chevron-up'"
              size="18"
              class="ms-auto"
            />
          </button>
          <div
            v-show="!mapCollapsed"
            class="ti-map-card__body"
          >
            <TerritoryMap />
          </div>
        </VCard>
      </section>

      <section class="ti-layout__tabs">
        <AppTabs
          v-if="showTabs"
          v-model="activeTab"
          :items="tabs"
          variant="underline"
          sync-mode="query"
          sync-key="tab"
          default-tab="overview"
          card
          tabs-class="px-2"
        />

        <!-- Before the first answer, and when it fails, there are no tabs to show. -->
        <VCard
          v-else
          elevation="0"
          border
        >
          <VCardText class="ti-state">
            <template v-if="firstLoad">
              <CatMascot
                pose="play"
                color="orange"
                :size="120"
              />
              <span class="text-medium-emphasis">{{ t("alkokh.territoryInsights.states.loading") }}</span>
            </template>
            <template v-else-if="ctx.state.value === 'not_deployed'">
              <CatMascot
                pose="sit"
                mood="grumpy"
                color="grey"
                :size="120"
              />
              <VAlert
                type="info"
                variant="tonal"
                icon="tabler-info-circle"
              >
                {{ t("alkokh.territoryInsights.states.not_deployed") }}
              </VAlert>
            </template>
            <template v-else>
              <CatMascot
                pose="sit"
                mood="grumpy"
                color="black"
                :size="120"
              />
              <VAlert
                type="error"
                variant="tonal"
              >
                {{ t("alkokh.territoryInsights.states.failed") }}
                <template #append>
                  <VBtn
                    size="small"
                    variant="text"
                    @click="ctx.load()"
                  >
                    {{ t("alkokh.territoryInsights.actions.retry") }}
                  </VBtn>
                </template>
              </VAlert>
            </template>
          </VCardText>
        </VCard>
      </section>
    </div>

    <TerritoryDetailDrawer />
    <TerritoryCoverageDialog />
  </div>
</template>

<style scoped>
.ti-page :deep(.app-tabs__window) {
  margin-block-start: 16px;
  overflow: visible;
}

.ti-head {
  position: relative;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  overflow: hidden;
  padding-block: 20px 12px;
  padding-inline: 20px;
  background:
    radial-gradient(circle at 88% 40%, rgba(var(--v-theme-error), 0.16), transparent 38%),
    radial-gradient(circle at 70% 0%, rgba(var(--v-theme-warning), 0.16), transparent 40%),
    radial-gradient(circle at 0% 0%, rgba(var(--v-theme-primary), 0.14), transparent 45%);
  background-size: 140% 140%;
  animation: ti-glow 14s ease-in-out infinite alternate;
}

.ti-head__text {
  flex: 1 1 320px;
}

.ti-head__art {
  position: relative;
  inline-size: 170px;
  block-size: 64px;
}

.ti-head__pin {
  position: absolute;
  font-size: 1.7rem;
  animation: ti-bob 3.2s ease-in-out infinite;
}

.ti-head__pin--a { inset-inline-start: 0; inset-block-start: 4px; }
.ti-head__pin--b { inset-inline-start: 46px; inset-block-start: 22px; animation-delay: 0.4s; }
.ti-head__pin--c { inset-inline-start: 92px; inset-block-start: 2px; animation-delay: 0.8s; }
.ti-head__pin--d { inset-inline-start: 136px; inset-block-start: 24px; animation-delay: 1.2s; }

.ti-live-chip {
  cursor: pointer;
}

.ti-live-chip__dot {
  display: inline-block;
  inline-size: 8px;
  block-size: 8px;
  margin-inline-end: 6px;
  border-radius: 50%;
  background: currentcolor;
  opacity: 0.6;
}

.ti-live-chip__dot--on {
  opacity: 1;
  animation: ti-beat 1.4s ease-in-out infinite;
}

.ti-live-chip__ago {
  margin-inline-start: 4px;
  opacity: 0.85;
  font-weight: 400;
}

.ti-filters {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
  padding-block: 14px;
  padding-inline: 20px;
  border-block-start: 1px solid rgba(var(--v-theme-on-surface), 0.08);
}

.ti-filters__item {
  flex: 1 1 170px;
  max-inline-size: 230px;
}

.ti-filters__item--wide {
  max-inline-size: 320px;
}

.ti-filters__metric {
  flex-wrap: wrap;
  block-size: auto !important;
}

.ti-filters__actions {
  display: flex;
  align-items: center;
  gap: 2px;
  margin-inline-start: auto;
}

/* Map beside the tabs on wide screens, above them otherwise. */
.ti-layout {
  display: grid;
  align-items: start;
  gap: 16px;
  grid-template-columns: minmax(0, 5fr) minmax(0, 7fr);
}

.ti-layout__map {
  position: sticky;
  inset-block-start: 84px;
  block-size: calc(100vh - 110px);
  min-block-size: 520px;
}

.ti-map-card {
  display: flex;
  flex-direction: column;
  block-size: 100%;
}

.ti-map-card__toggle {
  display: none;
  align-items: center;
  gap: 8px;
  padding-block: 10px;
  padding-inline: 14px;
  border: 0;
  background: transparent;
  color: inherit;
  cursor: pointer;
  font: inherit;
  font-weight: 600;
}

.ti-map-card__body {
  position: relative;
  flex: 1;
  min-block-size: 0;
}

.ti-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding-block: 48px;
}

@media (max-width: 1279px) {
  .ti-layout {
    grid-template-columns: minmax(0, 1fr);
  }

  .ti-layout__map {
    position: static;
    block-size: 440px;
    min-block-size: 0;
  }

  .ti-layout__map--collapsed {
    block-size: auto;
  }

  .ti-map-card__toggle {
    display: flex;
  }
}

@media (max-width: 959px) {
  .ti-layout__map {
    block-size: 340px;
  }

  .ti-head__art {
    display: none;
  }

  .ti-filters__item,
  .ti-filters__item--wide {
    max-inline-size: none;
  }
}

@keyframes ti-bob {
  50% { transform: translateY(-6px) rotate(-4deg); }
}

@keyframes ti-beat {
  50% {
    opacity: 0.35;
    transform: scale(0.7);
  }
}

@keyframes ti-glow {
  to { background-position: 100% 100%; }
}

@media (prefers-reduced-motion: reduce) {
  .ti-head,
  .ti-head__pin,
  .ti-live-chip__dot--on {
    animation: none;
  }
}
</style>

<!-- Motion shared by the tabs (they are separate components, so this cannot be scoped). -->
<style>
.ti-rise {
  animation: ti-rise 0.5s cubic-bezier(0.2, 0.9, 0.3, 1) both;
}

.ti-list-move,
.ti-list-enter-active,
.ti-list-leave-active {
  transition: transform 0.55s cubic-bezier(0.2, 0.9, 0.3, 1), opacity 0.4s ease;
}

.ti-list-enter-from,
.ti-list-leave-to {
  opacity: 0;
  transform: translateY(12px);
}

@keyframes ti-rise {
  from {
    opacity: 0;
    transform: translateY(14px);
  }
}

@media (prefers-reduced-motion: reduce) {
  .ti-rise {
    animation: none;
  }

  .ti-list-move,
  .ti-list-enter-active,
  .ti-list-leave-active {
    transition: none;
  }
}
</style>
